import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import type { CertificationDocument } from 'src/types/CertificationDocumentTypes';
import type {
  UnassignedSignatureDoc,
  SignatureDoc,
} from 'src/types/SignatureTypes';
import type { AppDispatch } from 'src/redux/store';
import type { RootState } from 'src/redux/rootReducerType';
import {
  analyzeDocumentsForSignatures,
  buildBulkRequestPayload,
} from 'src/components/Compliance/Certification/FileManagerV3/utils/signatureUtils';
import { flattenFMDocuments } from 'src/components/Compliance/Certification/FileManagerV3/utils/FileManagerV3Utils';
import {
  setCertificationDocuments,
  clearSelection,
  updateRenderedDocumentsInPlace,
} from 'src/redux/slices/Compliance/FileManagerV3Slice';
import type { SignatureRequestResultData } from 'src/components/Compliance/Certification/SignatureRequestResultDialog';

interface ESignCreateData {
  localId?: string;
}

interface SignaturesState {
  // Compliance E-Sign Modal
  complianceSignOpen: boolean;
  complianceSignDocument: CertificationDocument | null;

  // E-Sign Operations
  isSaving: boolean;
  createData: ESignCreateData | null;

  // Signature Request State (flattened)
  signatureRequestIsLoading: boolean;
  showAssignmentModal: boolean;
  unassignedDocs: UnassignedSignatureDoc[];
  assignedDocs: SignatureDoc[];
  selectedDocInViewer: UnassignedSignatureDoc | null;
  hoveredSignatureId: number | null;
  memberAssignments: Record<number, number>;
  signatureRequestError: string | null;

  // SUPPORT-12204 — Result/error dialog
  showResultDialog: boolean;
  resultDialogData: SignatureRequestResultData | null;
  resultDialogError: string | null;

  // SUPPORT-12204 — generating doc IDs to exclude from payload
  generatingDocIds: number[];
  generatingDocTitles: Record<number, string>;

  // Pending signature request (for individual document requests)
  pendingSignatureRequestDocs: CertificationDocument[] | null;

  // Pending compliance sign (for individual document compliance sign)
  pendingComplianceSignDoc: CertificationDocument | null;

  // Signature Lock State (flattened at root level)
  isRequestingLock: boolean;
  lockIsActive: boolean;
  lockIsOwnLock: boolean;
  lockByUserId: number | null;
  lockByUserName: string | null;
  lockAt: string | null;
  lockDocumentIds: number[]; // Document IDs that are currently locked
  lockSignatureType: 'tenant' | 'compliance' | null; // Type of signature being requested
}

const initialState: SignaturesState = {
  complianceSignOpen: false,
  complianceSignDocument: null,
  isSaving: false,
  createData: null,
  signatureRequestIsLoading: false,
  showAssignmentModal: false,
  unassignedDocs: [],
  assignedDocs: [],
  selectedDocInViewer: null,
  hoveredSignatureId: null,
  memberAssignments: {},
  signatureRequestError: null,
  pendingSignatureRequestDocs: null,
  pendingComplianceSignDoc: null,
  isRequestingLock: false,
  lockIsActive: false,
  lockIsOwnLock: false,
  lockByUserId: null,
  lockByUserName: null,
  lockAt: null,
  lockDocumentIds: [],
  lockSignatureType: null,

  // SUPPORT-12204 — Result/error dialog
  showResultDialog: false,
  resultDialogData: null,
  resultDialogError: null,
  generatingDocIds: [],
  generatingDocTitles: {},
};

const signaturesSlice = createSlice({
  name: 'signatures',
  initialState,
  reducers: {
    openComplianceSign: (
      state,
      action: PayloadAction<CertificationDocument>
    ) => {
      state.complianceSignOpen = true;
      state.complianceSignDocument = action.payload;
    },
    closeComplianceSign: (state) => {
      state.complianceSignOpen = false;
      state.complianceSignDocument = null;
    },
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setCreateData: (state, action: PayloadAction<ESignCreateData | null>) => {
      state.createData = action.payload;
    },
    resetESignData: (state) => {
      state.isSaving = false;
      state.createData = null;
    },

    // Signature request actions
    setSignatureRequestLoading: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.signatureRequestIsLoading = action.payload;
    },
    setShowAssignmentModal: (state, action: PayloadAction<boolean>): void => {
      state.showAssignmentModal = action.payload;
    },
    setUnassignedDocs: (
      state,
      action: PayloadAction<UnassignedSignatureDoc[]>
    ): void => {
      state.unassignedDocs = action.payload;
    },
    setAssignedDocs: (state, action: PayloadAction<SignatureDoc[]>): void => {
      state.assignedDocs = action.payload;
    },
    setSelectedDocInViewer: (
      state,
      action: PayloadAction<UnassignedSignatureDoc | null>
    ): void => {
      state.selectedDocInViewer = action.payload;
    },
    setHoveredSignatureId: (
      state,
      action: PayloadAction<number | null>
    ): void => {
      state.hoveredSignatureId = action.payload;
    },
    setMemberAssignment: (
      state,
      action: PayloadAction<{ isrId: number; memberId: number }>
    ): void => {
      state.memberAssignments[action.payload.isrId] = action.payload.memberId;
    },
    removeMemberAssignment: (state, action: PayloadAction<number>): void => {
      delete state.memberAssignments[action.payload];
    },
    setSignatureRequestError: (
      state,
      action: PayloadAction<string | null>
    ): void => {
      state.signatureRequestError = action.payload;
    },
    resetSignatureRequest: (state): void => {
      state.signatureRequestIsLoading = false;
      state.showAssignmentModal = false;
      state.unassignedDocs = [];
      state.assignedDocs = [];
      state.selectedDocInViewer = null;
      state.memberAssignments = {};
      state.signatureRequestError = null;
      // Result dialog is NOT cleared here — it persists until user dismisses.
    },

    // Signature lock actions
    setIsRequestingLock: (state, action: PayloadAction<boolean>): void => {
      state.isRequestingLock = action.payload;
    },

    setSignatureLock: (
      state,
      action: PayloadAction<{
        lockIsActive: boolean;
        lockIsOwnLock: boolean;
        lockByUserId: number | null;
        lockByUserName: string | null;
        lockAt: string | null;
        lockDocumentIds?: number[];
        lockSignatureType?: 'tenant' | 'compliance' | null;
      }>
    ): void => {
      state.lockIsActive = action.payload.lockIsActive;
      state.lockIsOwnLock = action.payload.lockIsOwnLock;
      state.lockByUserId = action.payload.lockByUserId;
      state.lockByUserName = action.payload.lockByUserName;
      state.lockAt = action.payload.lockAt;
      state.lockDocumentIds = action.payload.lockDocumentIds || [];
      state.lockSignatureType = action.payload.lockSignatureType || null;
    },

    clearSignatureLock: (state): void => {
      state.lockIsActive = false;
      state.lockIsOwnLock = false;
      state.lockByUserId = null;
      state.lockByUserName = null;
      state.lockAt = null;
      state.lockDocumentIds = [];
      state.lockSignatureType = null;
    },

    setPendingSignatureRequestDocs: (
      state,
      action: PayloadAction<CertificationDocument[] | null>
    ): void => {
      state.pendingSignatureRequestDocs = action.payload;
    },

    setPendingComplianceSignDoc: (
      state,
      action: PayloadAction<CertificationDocument | null>
    ): void => {
      state.pendingComplianceSignDoc = action.payload;
    },

    // SUPPORT-12204 — Result/error dialog
    setResultDialog: (
      state,
      action: PayloadAction<{
        data: SignatureRequestResultData | null;
        error: string | null;
      }>
    ): void => {
      state.showResultDialog = true;
      state.resultDialogData = action.payload.data;
      state.resultDialogError = action.payload.error;
    },
    closeResultDialog: (state): void => {
      state.showResultDialog = false;
      state.resultDialogData = null;
      state.resultDialogError = null;
    },

    setGeneratingDocIds: (
      state,
      action: PayloadAction<{ ids: number[]; titles: Record<number, string> }>
    ): void => {
      state.generatingDocIds = action.payload.ids;
      state.generatingDocTitles = action.payload.titles;
    },

    resetSignatures: () => initialState,
  },
});

export const {
  openComplianceSign,
  closeComplianceSign,
  setIsSaving,
  setCreateData,
  resetESignData,
  setSignatureRequestLoading,
  setShowAssignmentModal,
  setUnassignedDocs,
  setAssignedDocs,
  setSelectedDocInViewer,
  setHoveredSignatureId,
  setMemberAssignment,
  removeMemberAssignment,
  setSignatureRequestError,
  resetSignatureRequest,
  setIsRequestingLock,
  setSignatureLock,
  clearSignatureLock,
  setPendingSignatureRequestDocs,
  setPendingComplianceSignDoc,
  setResultDialog,
  closeResultDialog,
  setGeneratingDocIds,
  resetSignatures,
} = signaturesSlice.actions;

// Signature request thunks
export const initiateSignatureRequest = (
  docs: CertificationDocument[],
  certificationId: number
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    dispatch(setSignatureRequestLoading(true));
    dispatch(setSignatureRequestError(null));

    try {
      // The incoming docs come from selectedDocs, which is a stale snapshot —
      // it is NOT updated when a document finishes generating (only
      // renderedDocuments/certificationDocuments receive the WS regen-cleared
      // update). Re-hydrate each selected doc against the fresh
      // certificationDocuments tree so is_generating reflects current state.
      const freshFlat = flattenFMDocuments(
        getState().fileManagerV3.certificationDocuments
      );
      const freshById = new Map(freshFlat.map((d) => [d.id, d]));
      const hydratedDocs = docs.map((d) => freshById.get(d.id) ?? d);

      const analysis = analyzeDocumentsForSignatures(hydratedDocs);
      const genIds = Array.from(analysis.generatingDocIds);
      const genTitles: Record<number, string> = {};
      analysis.generatingDocTitles.forEach((title, id) => {
        genTitles[id] = title;
      });
      dispatch(setGeneratingDocIds({ ids: genIds, titles: genTitles }));

      if (analysis.hasUnassigned) {
        // Show modal for assignment
        dispatch(setUnassignedDocs(analysis.unassignedDocs));
        dispatch(setAssignedDocs(analysis.assignedDocs));
        dispatch(setShowAssignmentModal(true));
        dispatch(setSignatureRequestLoading(false));
      } else if (analysis.assignedDocs.length > 0) {
        // Direct request - no unassigned signatures
        await dispatch(
          submitBulkRequest(analysis.assignedDocs, {}, certificationId)
        );
      } else {
        // Nothing requestable — all generating/pending or have no ISRs.
        if (genIds.length > 0) {
          dispatch(
            setResultDialog({
              data: {
                detail: 'No signatures requested.',
                requestedCount: 0,
                skipped: genIds.map((id) => ({
                  document_id: id,
                  document_title: genTitles[id] || `Document ${id}`,
                  members: [
                    { name: '', reason: 'Document is still generating' },
                  ],
                })),
              },
              error: null,
            })
          );
        } else {
          dispatch(
            setResultDialog({
              data: null,
              error: 'No signatures found on selected documents',
            })
          );
        }
        dispatch(setSignatureRequestLoading(false));
      }
    } catch (error) {
      console.error('Error initiating signature request:', error);
      dispatch(
        setResultDialog({
          data: null,
          error: 'Failed to analyze documents for signatures',
        })
      );
      dispatch(setSignatureRequestLoading(false));
    }
  };
};

export const submitBulkRequest = (
  assignedDocs: SignatureDoc[],
  assignments: Record<number, number>,
  certificationId: number
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    dispatch(setSignatureRequestLoading(true));
    dispatch(setSignatureRequestError(null));

    try {
      const genIdSet = new Set(getState().signatures.generatingDocIds);
      const payload = buildBulkRequestPayload(
        assignments,
        assignedDocs,
        genIdSet
      );

      const response = await axios.post(
        `certification/file_manager_v3/${certificationId}/signatures/bulk_request/`,
        payload
      );

      // Update documents with the returned signature data
      interface UpdatedDocumentResponse {
        id: number;
        generation_status: string;
        form_signature_request?: CertificationDocument['form_signature_request'];
      }

      const updatedDocs: UpdatedDocumentResponse[] =
        response.data?.documents || [];
      if (updatedDocs.length > 0) {
        const state = getState();
        const currentDocs = state.fileManagerV3.certificationDocuments;

        const mergedDocs = currentDocs.map((doc) => {
          const updated = updatedDocs.find(
            (u: UpdatedDocumentResponse) => u.id === doc.id
          );
          if (!updated) {
            return doc;
          }
          return {
            ...doc,
            form_signature_request:
              updated.form_signature_request ?? doc.form_signature_request,
          };
        });

        dispatch(setCertificationDocuments(mergedDocs));

        // Also update renderedDocuments — the UI renders from this tree,
        // not certificationDocuments. Without this the file manager rows
        // don't reflect the new "requested" state after a successful send.
        const renderUpdates = updatedDocs.map((u: UpdatedDocumentResponse) => ({
          id: u.id,
          form_signature_request: u.form_signature_request,
        }));
        dispatch(updateRenderedDocumentsInPlace(renderUpdates));
      }

      // Success: close modal, clear selection, show result dialog
      dispatch(setShowAssignmentModal(false));
      dispatch(resetSignatureRequest());
      dispatch(clearSelection());
      dispatch(setSignatureRequestLoading(false));

      // SUPPORT-12204: surface partition skips in a dialog.
      const skipped: SignatureRequestResultData['skipped'] = (
        response.data?.skipped || []
      ).slice();

      // Include generating docs that were filtered out client-side.
      const skippedIds = getState().signatures.generatingDocIds;
      const skippedTitles = getState().signatures.generatingDocTitles;
      if (skippedIds.length > 0) {
        skipped.push({
          document_id: 0,
          members: skippedIds.map((id) => ({
            name: skippedTitles[id] || `Document ${id}`,
            reason: 'Document is still generating',
          })),
        });
      }

      dispatch(
        setResultDialog({
          data: {
            detail: response.data?.detail ?? 'Signature requests sent.',
            requestedCount: response.data?.requested_count ?? 0,
            skipped,
          },
          error: null,
        })
      );
    } catch (error) {
      console.error('Error submitting bulk signature request:', error);

      dispatch(setIsRequestingLock(false));
      dispatch(setPendingSignatureRequestDocs(null));

      // SUPPORT-12204: show error detail in a dialog rather than
      // the previous inline-text / generic fallback.
      //
      // The Axios interceptor (utils/axios.tsx:79-80) replaces the raw
      // error with { ...error.response.data, status }, stripping the
      // response wrapper.  So detail may live on the error object
      // directly OR nested under error.response.data.
      let errorDetail = 'Failed to submit signature request';
      if (error && typeof error === 'object') {
        const err = error as Record<string, unknown>;

        // Path 1: Axios interceptor spread — detail at top level.
        if (typeof err.detail === 'string') {
          errorDetail = err.detail;
        }
        // Path 2: raw Axios error — detail under response.data.
        else if (
          'response' in err &&
          err.response &&
          typeof err.response === 'object' &&
          'data' in err.response
        ) {
          const data = (err.response as Record<string, unknown>).data;
          if (data && typeof data === 'object' && 'detail' in data) {
            errorDetail = (data as { detail: string }).detail;
          }
        }
      }

      dispatch(
        setResultDialog({
          data: null,
          error: errorDetail,
        })
      );
      dispatch(setSignatureRequestLoading(false));
      // Don't close the assignment modal — let the user retry after
      // dismissing the dialog.
    }
  };
};

export default signaturesSlice.reducer;
